import Image from 'next/image';
import useSWR from 'swr';
import Link from 'next/link';
import { useMemo } from 'react';
import { TacoRating } from '@appsumo/dorado-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { DISCUSSION } from '~/lib/discussions/constants';
import { fetchJson } from '~/lib/fetch';
import StatusTags from '~/components/discussions/DiscussionStatus';
import { ReviewPageProps } from '~/lib/discussions/types';
import { PDPContext } from '~/lib/product/context';
import { useOneTap } from '~/lib/script';
import { useUserStatus } from '~/hooks/userStatus';

import {
  ButtonsV2,
  ActionsV2,
  ChildrenV2,
  UserMetaV2,
} from '~/components/discussions';
import DiscussionSidebar from '~/components/discussions/DiscussionSidebar';
import { ReviewSeo } from '~/components/seo';
import { Breadcrumb, Breadcrumbs } from '../ui';
import { ReviewCard } from './card/ReviewCard';

export const ReviewPageComponent = ({
  deal,
  discussion: fallbackData,
  discussionId,
  useReviews2_5UI = false,
}: ReviewPageProps) => {
  useOneTap();

  // API V1
  const url = `/api/v2/deals/${deal.id}/reviews/${discussionId}/`;

  const { data: commentData, mutate } = useSWR(url, fetchJson, {
    fallbackData,
  });
  const comments = commentData?.comments ? commentData.comments : commentData;
  const review = comments[0];

  const { userStatus } = useUserStatus(deal.id);
  const { user } = review;

  const items: Breadcrumb[] = useMemo(
    () => [
      { href: `/products/${deal.slug}/`, label: deal.public_name },
      {
        href: `/products/${deal.slug}/reviews/`,
        label: `${deal.public_name} Rating & Reviews`,
      },
      { label: review.title },
    ],
    [deal.public_name, deal.slug, review.title],
  );

  return (
    <>
      <ReviewSeo review={review} deal={deal} />
      <PDPContext.Provider value={{ deal, userStatus }}>
        <div className="mx-auto w-full max-w-7xl grow px-4 md:my-12">
          <Breadcrumbs items={items} className="mt-6 md:mb-4 lg:mb-6 xl:px-8" />

          {/* Mobile header */}
          <div className="-mx-8 mb-4 flex flex-wrap justify-between gap-x-4 border-b p-4 px-8 sm:hidden">
            <h3 className="font-header text-2xl font-semibold">
              {deal.public_name}
            </h3>
            <Link
              href={`/products/${deal.slug}/`}
              className="flex items-center text-blue-600"
            >
              Product details
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                className="ml-2"
              />
            </Link>
          </div>

          <article className="mx-auto flex max-w-6xl gap-x-8 gap-y-6 max-lg:flex-col">
            <div className="grow">
              {!useReviews2_5UI && userStatus?.user_moderator && (
                <StatusTags
                  isApproved={review.approved}
                  isResolved={review.resolved}
                  discussionStatus={review.status?.toLowerCase()}
                />
              )}

              {!useReviews2_5UI && (
                <header className="mb-4">
                  <h1 className={`mb-2 font-header text-3xl font-bold`}>
                    {review.rating && `${deal.public_name} Review: `}
                    {review.title}
                  </h1>
                  {review.rating && (
                    <TacoRating
                      averageRating={review.rating}
                      href={`/products/${deal.slug}/#reviews`}
                      size="md"
                      className="mt-4"
                    />
                  )}
                </header>
              )}

              <main>
                {useReviews2_5UI && (
                  <ReviewCard
                    review={review}
                    mutateComment={mutate}
                    partnersIds={deal.partner_user_ids ?? []}
                    expandCard
                  />
                )}

                {!useReviews2_5UI && (
                  <>
                    <p className="leading-relaxed">{review.comment}</p>
                    <div className="mt-4 flex gap-x-4">
                      <Link href={`/profile/${user.username}/`} target="_blank">
                        <Image
                          src={user.avatar}
                          alt={user.username}
                          width={70}
                          height={70}
                          unoptimized
                          className="h-[70px] w-[70px] rounded-full max-sm:h-[40px] max-sm:w-[40px]"
                        />
                      </Link>
                      <UserMetaV2
                        user={review.user}
                        posted={review.created}
                        purchased={review.purchased}
                      />
                    </div>
                    <>
                      <ButtonsV2
                        comment={review}
                        mutateComment={mutate}
                        isReview
                      />
                      <ActionsV2
                        comment={review}
                        mutateComment={mutate}
                        moderator={userStatus?.user_moderator}
                      />
                      {review.children && review.children.length > 0 && (
                        <div className="pt-10">
                          <ChildrenV2
                            root={review}
                            comments={review.children}
                            mutateComment={mutate}
                            moderator={userStatus?.user_moderator}
                            isReview
                          />
                        </div>
                      )}
                    </>
                  </>
                )}
              </main>
            </div>

            {useReviews2_5UI && (
              <details className="group lg:hidden">
                <summary className="mb-4 flex items-center justify-between border-b border-gray-200 pb-4 font-header font-semibold text-black-pearl">
                  Ratings
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="transition-transform group-open:rotate-180"
                  />
                </summary>
                <DiscussionSidebar
                  deal={deal}
                  discussionSlug={DISCUSSION.REVIEWS}
                />
              </details>
            )}

            <div className={useReviews2_5UI ? 'hidden lg:block' : ''}>
              <DiscussionSidebar
                deal={deal}
                discussionSlug={DISCUSSION.REVIEWS}
              />
            </div>
          </article>
        </div>
      </PDPContext.Provider>
    </>
  );
};
